import _ from 'lodash';
import React from 'react';

import Statuses from '../model/Statuses';

export function makeListCacheUpdater(
  queryGql,
  {
    variables = {},
    itemsEqual = null,
    itemDeleted = null,
    itemDeletedByStatus = false,
    sourceItemPath = null,
    destinationListPath = null,
    skip = null,
    customUpdater = null,
    isExtractArray = true,
  } = {},
) {
  if (itemDeletedByStatus) {
    itemDeleted = item => item.status === Statuses.Deleted.value;
  }

  return (proxy, { data: mutationData }) => {
    const mutatedItem = extractItem(mutationData, sourceItemPath);
    if (skip && skip(mutatedItem)) {
      return;
    }

    let cachedQueryData;

    try {
      cachedQueryData = proxy.readQuery({ query: queryGql, variables });
    } catch (e) {
      // TODO: check if query exists some other way?
      console.warn(`Your written apollo cache updater isn't works :)`);
      return;
    }

    const cachedList = extractList(
      cachedQueryData,
      destinationListPath,
      isExtractArray,
    );

    if (customUpdater) {
      customUpdater(cachedList, mutatedItem, itemsEqual, itemDeleted);
    } else {
      if (Array.isArray(mutatedItem)) {
        for (const item of mutatedItem) {
          updateListWithItem(cachedList, item, itemsEqual, itemDeleted);
        }
      } else {
        updateListWithItem(cachedList, mutatedItem, itemsEqual, itemDeleted);
      }
    }

    proxy.writeQuery({ query: queryGql, data: cachedQueryData, variables });
  };
}

export function makeListItemUpdater(
  query,
  itemMapper,
  { variables = {} } = {},
) {
  return (proxy, { data: mutationResponse }) => {
    const data = proxy.readQuery({ query, variables });

    for (const key of Object.keys(data)) {
      const value = data[key];
      if (Array.isArray(value)) {
        data[key] = value.map(item => itemMapper(item, mutationResponse));
      }
    }
    proxy.writeQuery({ query, variables, data });
  };
}

export function composeUpdaters(...updaters) {
  return (...args) => {
    for (const updater of updaters) {
      updater(...args);
    }
  };
}

function updateListWithItem(list, mutatedItem, itemsEqual, itemDeleted) {
  if (!itemDeleted) {
    itemDeleted = item => item.deleted;
  }

  if (itemDeleted(mutatedItem)) {
    _.remove(list, makeItemMatcher(mutatedItem, itemsEqual));
    return;
  }

  const index = _.findIndex(list, makeItemMatcher(mutatedItem, itemsEqual));

  if (index >= 0) {
    list[index] = mutatedItem;
  } else {
    list.push(mutatedItem);
  }
}

// TODO: bicycle aka reinvented wheel; use apollo's Mutation component
export const withStateMutation = ({
  name = 'mutate',
  throwError = false,
} = {}) => WrappedComponent =>
  class extends React.Component {
    static displayName = `withStateMutation$${name}`;

    state = { loading: false, error: null, result: null };

    loadingProperty = `${name}Loading`;
    errorProperty = `${name}Error`;
    resultProperty = `${name}Result`;

    handleMutation(options) {
      this.setState({
        loading: true,
        error: null,
        result: null,
      });
      return this.props[name](options)
        .then(result => {
          this.setState({
            loading: false,
            error: null,
            result,
          });
        })
        .catch(err => {
          this.setState({
            loading: false,
            error: err,
            result: null,
          });
          if (throwError) {
            throw err;
          }
        });
    }

    render() {
      const props = {
        ...this.props,
        [name]: this.handleMutation.bind(this),
        [this.loadingProperty]: this.state.loading,
        [this.errorProperty]: this.state.error,
        [this.resultProperty]: this.state.result,
      };

      return <WrappedComponent {...props} />;
    }
  };

function extractList(data, path, isExtractArray) {
  const item = extractItem(data, path);
  if (!item) {
    throw new Error(
      `apollo cache helper: extractList: nothing found on [${path}] in `,
      data,
    );
  }

  if (isExtractArray && !Array.isArray(item)) {
    throw new Error(
      `apollo cache helper: extractList: item is not a list on [${path}] in `,
      data,
    );
  }

  return item;
}

function extractItem(data, path) {
  if (path) {
    if (typeof path === 'function') {
      return path(data);
    }
    return _.get(data, path);
  }

  const keys = Object.keys(data);
  if (keys.length !== 1) {
    throw new Error(
      `apollo cache helper: extractItem: item shape is unexpected in `,
      data,
    );
  }

  return data[keys[0]];
}

function makeItemMatcher(targetItem, itemsEqual) {
  if (itemsEqual) {
    return item => itemsEqual(item, targetItem);
  }

  if (!targetItem.id) {
    throw new Error(
      'apollo cache helper: makeItemMatcher: provided target item has no id',
    );
  }

  return item => item.id === targetItem.id;
}
